import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import QrScanner from 'qr-scanner';
import { FC, MutableRefObject, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/shared/Button';
import { Icons, IconsTypes } from '@/components/shared/Icons';
import { useToggle } from '@/hooks/useToggle';

enum ScannerErrorType {
  ValidationError = 'validation-error',
  PermissionsError = 'permissions-error',
}

interface IQrScannerProps {
  shouldReload: boolean;
  toggleShouldReload: () => void;
}

export const QrScannerComponent: FC<IQrScannerProps> = ({ shouldReload, toggleShouldReload }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef: MutableRefObject<QrScanner | null> = useRef(null);

  const [errorType, setErrorType] = useState<ScannerErrorType | null>(null);
  const [isLoading, toggleIsLoading] = useToggle();

  const { t } = useTranslation();
  const { push } = useRouter();

  const onScanSuccess = async (link: string) => {
    if (link?.startsWith(window.location.origin)) {
      await push(link);
    } else {
      setErrorType(ScannerErrorType.ValidationError);
    }
  };

  const cleanUpScanner = () => {
    scannerRef.current?.stop();
    scannerRef.current?.destroy();
    scannerRef.current = null;
  };

  const startScanner = async () => {
    try {
      toggleIsLoading();

      scannerRef.current = new QrScanner(
        videoRef.current as HTMLVideoElement,
        async ({ data }) => {
          cleanUpScanner();
          await onScanSuccess(data);
        },
        {
          preferredCamera: 'environment',
          highlightCodeOutline: true,
          highlightScanRegion: true,
        },
      );

      scannerRef.current?.setInversionMode('both');

      await scannerRef.current?.start();

      toggleIsLoading();
    } catch (e) {
      cleanUpScanner();
      setErrorType(ScannerErrorType.PermissionsError);
      toggleShouldReload();
    }
  };

  const restartScanner = async () => {
    setErrorType(null);
    await startScanner();
  };

  const onErrorBtnClick = async () => {
    if (shouldReload) {
      return window.location.reload();
    }

    await restartScanner();
  };

  useEffect(() => {
    startScanner();

    return function () {
      cleanUpScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative rounded-md border p-5">
      <video ref={videoRef} />

      {isLoading && (
        <p className="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-white">
          {t('shared.loading')}
        </p>
      )}

      {errorType && (
        <div className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-center space-y-4 bg-white">
          <p>{t(`modals.qr-scanner.${errorType}`)}</p>

          <Button
            type="button"
            className="flex items-center justify-center space-x-4 font-bold"
            onClick={onErrorBtnClick}
          >
            {errorType === ScannerErrorType.ValidationError && (
              <>
                {Icons({ name: IconsTypes.QRCODE })}
                <span>{t('buttons.scan-QR')}</span>
              </>
            )}

            {errorType === ScannerErrorType.PermissionsError && <span>{t('buttons.try-again')}</span>}
          </Button>
        </div>
      )}
    </div>
  );
};
